import React, { useEffect, useMemo, useState } from "react";
import { useCurrentUserQuery } from "../../../../api/queries/useUserQuery";
import { HTTP_ENDPOINTS, USER_ROLE_NAMES } from "../../../../consts/api";
import { http } from "../../../../services/HttpService";
import { uiNotification } from "../../../../services/UINotificationService";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import styles from "./boomrx.module.scss";

const PAGE_SIZE = 40;

function errorMessage(err, fallback) {
  const data = err && err.response && err.response.data;
  if (data && data.data && data.data.detail) {
    return data.data.detail;
  }
  if (data && data.message && data.message.indexOf("wellsync_") !== 0) {
    return data.message;
  }
  return fallback;
}

function money(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function percent(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `${n}%`;
}

function shippingLabel(method) {
  return String(method || "")
    .replace(/^SHIPPING_METHOD_/, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function BoomRXPriceList() {
  const { data: user, isLoading: userLoading } = useCurrentUserQuery();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (user?.role !== USER_ROLE_NAMES.admin) return undefined;
    let cancelled = false;
    setLoading(true);
    http
      .get(HTTP_ENDPOINTS.boomrx.priceList())
      .then((res) => {
        if (cancelled) return;
        setList((res.data && res.data.data) || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setList(null);
        uiNotification.error(errorMessage(err, "Failed to load BoomRX prices"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  const rows = list && Array.isArray(list.rows) ? list.rows : [];
  const shipping =
    list && Array.isArray(list.shippingFees) ? list.shippingFees : [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.drugName, row.concentration, row.pharmacyId, row.unit]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, query]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE,
  );
  const cardFee = percent(list && list.creditCardFeePercent);
  const convenienceFee = percent(list && list.convenienceFeePercent);

  if (userLoading || user?.role !== USER_ROLE_NAMES.admin) {
    return null;
  }

  return (
    <section className={styles.priceList}>
      <h3 className={styles.dashboardSectionTitle}>Drug price list</h3>
      <p className={styles.priceListLead}>
        BoomRX medication prices for this account. The same catalog applies to
        every connected clinic.
        {cardFee || convenienceFee
          ? ` Card fee ${cardFee || "—"}. Convenience fee ${
              convenienceFee || "—"
            }.`
          : ""}
      </p>
      {loading ? (
        <CircularProgress />
      ) : list && list.connected === false ? (
        <p className={styles.dashboardEmpty}>
          Connect a BoomRX clinic to load prices.
        </p>
      ) : (
        <>
          <input
            className={styles.input}
            type="search"
            placeholder="Search medication, concentration, or pharmacy"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
          />
          <p className={styles.priceListCount}>
            {filtered.length} price{filtered.length === 1 ? "" : "s"}
            {query.trim() ? ` matching “${query.trim()}”` : ""}
          </p>
          {visible.length === 0 ? (
            <p className={styles.dashboardEmpty}>
              No medications match that search.
            </p>
          ) : (
            <div className={styles.priceTableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Medication</th>
                    <th>Concentration</th>
                    <th>Pharmacy</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row, index) => (
                    <tr
                      key={`${row.drugName}-${row.concentration}-${row.pharmacyId}-${index}`}
                    >
                      <td>{row.drugName || "Unlisted medication"}</td>
                      <td>
                        {row.concentration || "—"}
                        {row.unit ? ` (${row.unit})` : ""}
                      </td>
                      <td>{row.pharmacyId || "—"}</td>
                      <td>{money(row.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filtered.length > PAGE_SIZE ? (
            <div className={styles.pricePager}>
              <button
                type="button"
                className={styles.btnGhost}
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {safePage + 1} of {pageCount}
              </span>
              <button
                type="button"
                className={styles.btnGhost}
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
          {shipping.length > 0 ? (
            <>
              <h3 className={styles.priceShippingTitle}>Shipping fees</h3>
              <div className={styles.priceTableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Pharmacy</th>
                      <th>Method</th>
                      <th>Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipping.map((fee, index) => (
                      <tr key={`${fee.pharmacyId}-${fee.method}-${index}`}>
                        <td>{fee.pharmacyId || "—"}</td>
                        <td>{shippingLabel(fee.method) || "—"}</td>
                        <td>{money(fee.fee)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}
